import React, { useState, useEffect, useCallback } from 'react';

const Icon = ({ d, size = 16, strokeWidth = 2, className = '' }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    globe: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M2 12h20', 'M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z'],
    plus: ['M12 5v14', 'M5 12h14'],
    search: ['M21 21l-6-6m2-5a7 7 0 1 1-14 0 7 7 0 0 1 14 0z'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6', 'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2', 'M10 11v6', 'M14 11v6'],
};

const API = '/api/countries';

export default function Countries() {
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ name: '', code: '', currency: '', engCurrency: '', exchangeRate: 1, locale: '', taxRate: 0, isActive: true });

    const fetchCountries = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(API);
            const data = await res.json();
            setCountries(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Fetch countries error:", err);
            setCountries([]);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchCountries();
    }, [fetchCountries]);

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                name: item.name,
                code: item.code,
                currency: item.currency || '',
                engCurrency: item.engCurrency || '',
                exchangeRate: item.exchangeRate || 1,
                locale: item.locale || '',
                taxRate: item.taxRate,
                isActive: item.isActive
            });
        } else {
            setEditingItem(null);
            setFormData({ name: '', code: '', currency: '', engCurrency: '', exchangeRate: 1, locale: '', taxRate: 0, isActive: true });
        }
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const url = editingItem ? `${API}/${editingItem._id}` : API;
            const method = editingItem ? 'PUT' : 'POST';
            const res = await fetch(url, {
                method: method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            if (res.ok) {
                setShowModal(false);
                fetchCountries();
            }
        } catch (err) {
            console.error("Save country error:", err);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this country?")) return;
        try {
            const res = await fetch(`${API}/${id}`, { method: 'DELETE' });
            if (res.ok) fetchCountries();
        } catch (err) {
            console.error("Delete country error:", err);
        }
    };

    const filteredCountries = countries.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="countries-page">
            <div className="page-header">
                <div>
                    <h1>Country Management</h1>
                    <p>Configure regional settings, dialing codes, tax rates, and status.</p>
                </div>
                <button className="btn-primary" onClick={() => handleOpenModal()}>
                    <Icon d={icons.plus} size={18} /> Add Country
                </button>
            </div>

            <div className="filters-bar">
                <div className="search-input-wrap">
                    <Icon d={icons.search} size={16} className="search-icon-inline" />
                    <input
                        type="text"
                        placeholder="Search countries..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="premium-table-container">
                {loading ? (
                    <div className="empty-state">Loading countries...</div>
                ) : filteredCountries.length === 0 ? (
                    <div className="empty-state">No countries found.</div>
                ) : (
                    <table className="premium-table">
                        <thead>
                            <tr>
                                <th>Country Name</th>
                                <th>Country Code</th>
                                <th>Tax Rate (%)</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCountries.map(c => (
                                <tr key={c._id}>
                                    <td style={{ fontWeight: 600 }}>{c.name}</td>
                                    <td><span className="code-badge">{c.code}</span></td>
                                    <td>{c.taxRate}%</td>
                                    <td>
                                        <span className={`status-pill ${c.isActive ? 'status-active' : 'status-inactive'}`}>
                                            {c.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <button className="icon-btn edit" onClick={() => handleOpenModal(c)}>
                                            <Icon d={icons.edit} size={16} />
                                        </button>
                                        <button className="icon-btn delete" onClick={() => handleDelete(c._id)}>
                                            <Icon d={icons.trash} size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>{editingItem ? 'Edit Country' : 'Add New Country'}</h2>
                            <button className="close-btn" onClick={() => setShowModal(false)}>
                                <Icon d={['M18 6L6 18', 'M6 6l12 12']} size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSave}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="form-label">Country Name</label>
                                    <input
                                        className="form-input"
                                        placeholder="e.g., India"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Country Dialing Code</label>
                                    <input
                                        className="form-input"
                                        placeholder="e.g., +91"
                                        value={formData.code}
                                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Currency Symbol</label>
                                        <input
                                            className="form-input"
                                            placeholder="e.g., ₹ or $"
                                            value={formData.currency}
                                            onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Currency Code</label>
                                        <input
                                            className="form-input"
                                            placeholder="e.g., INR or USD"
                                            value={formData.engCurrency}
                                            onChange={(e) => setFormData({ ...formData, engCurrency: e.target.value })}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Tax Rate (%)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="form-input"
                                        placeholder="e.g., 18"
                                        value={formData.taxRate}
                                        onChange={(e) => setFormData({ ...formData, taxRate: parseFloat(e.target.value) })}
                                        required
                                    />
                                </div>
                                <div className="toggle-group">
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={formData.isActive}
                                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                    <span className="toggle-label">Active Operational Status</span>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-save">{editingItem ? 'Update Country' : 'Save Country'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .countries-page {
                    padding: 30px;
                    flex: 1;
                    overflow-y: auto;
                }
                .page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 30px;
                }
                .page-header h1 {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0 0 5px 0;
                }
                .page-header p {
                    color: #64748b;
                    margin: 0;
                }
                .code-badge {
                    background: #f1f5f9;
                    color: #1e3a8a;
                    padding: 4px 10px;
                    border-radius: 6px;
                    font-family: 'JetBrains Mono', monospace;
                    font-weight: 700;
                    font-size: 13px;
                }
                .status-inactive {
                    background: #fee2e2;
                    color: #991b1b;
                }
                .theme-dark .status-inactive {
                    background: rgba(153, 27, 27, 0.2);
                    color: #f87171;
                }
                .theme-dark .code-badge {
                    background: #1e293b;
                    color: #60a5fa;
                }
                .theme-dark .page-header h1 { color: #f8fafc; }
            `}</style>
        </div>
    );
}
